import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLocation } from '../context/LocationContext';
import { supabase } from '../services/supabase';
import LocationBanner from '../components/LocationBanner';

const STARS = [5, 4, 3, 2, 1];

function StarDisplay({ rating }) {
  return (
    <span>
      {[1, 2, 3, 4, 5].map(s => (
        <span key={s} style={{ color: s <= rating ? '#F59E0B' : '#D1D5DB', fontSize: 14 }}>★</span>
      ))}
    </span>
  );
}

export default function ReviewsPage() {
  const { business } = useAuth();
  const { activeLocation } = useLocation();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [toast, setToast] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null); // id of review pending delete

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const loadReviews = useCallback(async () => {
    if (!business?.id) return;
    setLoading(true);

    let query = supabase
      .from('reviews')
      .select('*')
      .eq('business_id', business.id)
      .order('created_at', { ascending: false });

    // Filter by active location if one is selected
    if (activeLocation?.id) {
      query = query.eq('location_id', activeLocation.id);
    }

    const { data, error } = await query;
    if (!error) setReviews(data || []);
    setLoading(false);
  }, [business?.id, activeLocation?.id]);

  useEffect(() => { loadReviews(); }, [loadReviews]);

  const handleFlag = async (id, currentFlag) => {
    const { error } = await supabase
      .from('reviews')
      .update({ is_flagged: !currentFlag })
      .eq('id', id);
    if (!error) {
      setReviews(prev => prev.map(r => r.id === id ? { ...r, is_flagged: !currentFlag } : r));
      showToast(currentFlag ? '✅ Şikayət geri alındı' : '🚩 Rəy gizlədildi');
    }
  };

  const handleDelete = async (id) => {
    const { error } = await supabase
      .from('reviews')
      .delete()
      .eq('id', id);
    if (!error) {
      setReviews(prev => prev.filter(r => r.id !== id));
      showToast('🗑️ Rəy silindi');
    } else {
      showToast('Xəta baş verdi', 'error');
    }
    setDeleteConfirm(null);
  };

  const avgRating = reviews.length > 0
    ? (reviews.reduce((s, r) => s + r.rating, 0) / reviews.length).toFixed(1)
    : '—';

  const filtered = reviews.filter(r => {
    if (filter === 'flagged') return r.is_flagged;
    if (filter === 'all') return true;
    return r.rating === parseInt(filter);
  });

  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: 60, color: '#888' }}>
      Yüklənir...
    </div>
  );

  return (
    <div style={{ padding: 24, maxWidth: 860, margin: '0 auto' }}>
      {toast && (
        <div style={{
          position: 'fixed', top: 20, right: 20, zIndex: 9999,
          background: toast.type === 'success' ? '#e8f5e9' : '#ffebee',
          color: toast.type === 'success' ? '#2e7d32' : '#c62828',
          padding: '12px 20px', borderRadius: 10, fontWeight: 600, fontSize: 14,
          boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
        }}>{toast.msg}</div>
      )}

      {/* Delete confirmation modal */}
      {deleteConfirm && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9998,
        }}>
          <div style={{
            background: '#fff', borderRadius: 16, padding: 28, maxWidth: 360, width: '90%',
            boxShadow: '0 8px 40px rgba(0,0,0,0.18)',
          }}>
            <div style={{ fontSize: 36, textAlign: 'center', marginBottom: 12 }}>🗑️</div>
            <h3 style={{ textAlign: 'center', fontWeight: 800, marginBottom: 8 }}>Rəyi sil</h3>
            <p style={{ textAlign: 'center', color: '#666', fontSize: 14, marginBottom: 24, lineHeight: 1.6 }}>
              Bu rəy tamamilə silinəcək. Bu əməliyyat geri alına bilməz.
            </p>
            <div style={{ display: 'flex', gap: 10 }}>
              <button
                onClick={() => setDeleteConfirm(null)}
                style={{
                  flex: 1, padding: '10px 0', borderRadius: 10, border: '1px solid #e5e7eb',
                  background: '#f9fafb', fontWeight: 600, cursor: 'pointer', fontSize: 14,
                }}>
                Ləğv et
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm)}
                style={{
                  flex: 1, padding: '10px 0', borderRadius: 10, border: 'none',
                  background: '#ef4444', color: '#fff', fontWeight: 700, cursor: 'pointer', fontSize: 14,
                }}>
                Sil
              </button>
            </div>
          </div>
        </div>
      )}

      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 26, fontWeight: 800, color: '#1A1A2E' }}>⭐ Müştəri Rəyləri</h1>
        <p style={{ color: '#888' }}>Müştərilərin rəylərini izləyin, uygunsuz rəyləri gizlədin və ya silin</p>
      </div>

      <LocationBanner />

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 24 }}>
        <div style={{ background: '#fff', borderRadius: 14, padding: 20,
          boxShadow: '0 2px 12px rgba(0,0,0,0.07)', textAlign: 'center' }}>
          <div style={{ fontSize: 32, fontWeight: 800, color: '#F59E0B' }}>{avgRating}</div>
          <div style={{ fontSize: 13, color: '#888', marginTop: 4 }}>Ortalama Reytinq</div>
        </div>
        <div style={{ background: '#fff', borderRadius: 14, padding: 20,
          boxShadow: '0 2px 12px rgba(0,0,0,0.07)', textAlign: 'center' }}>
          <div style={{ fontSize: 32, fontWeight: 800, color: '#667eea' }}>{reviews.length}</div>
          <div style={{ fontSize: 13, color: '#888', marginTop: 4 }}>Ümumi Rəy</div>
        </div>
        <div style={{ background: '#fff', borderRadius: 14, padding: 20,
          boxShadow: '0 2px 12px rgba(0,0,0,0.07)', textAlign: 'center' }}>
          <div style={{ fontSize: 32, fontWeight: 800, color: '#ef4444' }}>
            {reviews.filter(r => r.is_flagged).length}
          </div>
          <div style={{ fontSize: 13, color: '#888', marginTop: 4 }}>Gizlədilmiş</div>
        </div>
      </div>

      {/* Rating breakdown */}
      <div style={{ background: '#fff', borderRadius: 14, padding: 20, marginBottom: 24,
        boxShadow: '0 2px 12px rgba(0,0,0,0.07)' }}>
        <h3 style={{ fontWeight: 700, marginBottom: 14, fontSize: 15 }}>Ulduz Bölgüsü</h3>
        {STARS.map(star => {
          const count = reviews.filter(r => r.rating === star).length;
          const pct = reviews.length > 0 ? (count / reviews.length) * 100 : 0;
          return (
            <div key={star} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
              <span style={{ fontSize: 13, fontWeight: 600, width: 24, color: '#555' }}>{star}★</span>
              <div style={{ flex: 1, height: 8, background: '#f0f0f0', borderRadius: 4, overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${pct}%`, background: '#F59E0B', borderRadius: 4, transition: 'width 0.4s' }} />
              </div>
              <span style={{ fontSize: 12, color: '#888', width: 32 }}>{count}</span>
            </div>
          );
        })}
      </div>

      {/* Filter tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        {[
          { key: 'all',     label: 'Hamısı' },
          { key: '5',       label: '5 ★' },
          { key: '4',       label: '4 ★' },
          { key: '3',       label: '3 ★' },
          { key: '2',       label: '2 ★' },
          { key: '1',       label: '1 ★' },
          { key: 'flagged', label: '🚩 Gizlədilmiş' },
        ].map(f => (
          <button key={f.key} onClick={() => setFilter(f.key)}
            style={{
              padding: '6px 14px', borderRadius: 20, border: 'none', cursor: 'pointer',
              fontSize: 13, fontWeight: 600,
              background: filter === f.key ? '#667eea' : '#f1f5f9',
              color: filter === f.key ? '#fff' : '#555',
            }}>
            {f.label}
          </button>
        ))}
      </div>

      {/* Reviews list */}
      {filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 48, color: '#aaa' }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>💬</div>
          <p>Bu filtrdə rəy yoxdur</p>
        </div>
      ) : (
        filtered.map(r => (
          <div key={r.id} style={{
            background: '#fff', borderRadius: 14, padding: 18, marginBottom: 12,
            boxShadow: '0 2px 12px rgba(0,0,0,0.07)',
            opacity: r.is_flagged ? 0.65 : 1,
            borderLeft: r.is_flagged ? '4px solid #ef4444' : '4px solid transparent',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{
                  width: 38, height: 38, borderRadius: '50%', background: '#667eea',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: '#fff', fontWeight: 700, fontSize: 15,
                }}>
                  {(r.author_name || 'İ')[0].toUpperCase()}
                </div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 14, color: '#1a1a1a' }}>{r.author_name || 'İstifadəçi'}</div>
                  <div style={{ fontSize: 12, color: '#aaa' }}>
                    {r.created_at ? new Date(r.created_at).toLocaleDateString('az-AZ') : ''}
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <StarDisplay rating={r.rating} />
                <button
                  onClick={() => handleFlag(r.id, r.is_flagged)}
                  title={r.is_flagged ? 'Gizlətməni geri al' : 'Gizlət'}
                  style={{
                    padding: '5px 12px', borderRadius: 8, border: 'none', cursor: 'pointer',
                    fontSize: 12, fontWeight: 600,
                    background: r.is_flagged ? '#fee2e2' : '#f1f5f9',
                    color: r.is_flagged ? '#ef4444' : '#64748b',
                  }}>
                  {r.is_flagged ? '✓ Gizlədilmiş' : '🚩 Gizlət'}
                </button>
                <button
                  onClick={() => setDeleteConfirm(r.id)}
                  title="Rəyi sil"
                  style={{
                    padding: '5px 10px', borderRadius: 8, border: 'none', cursor: 'pointer',
                    fontSize: 13, background: '#fff0f0', color: '#dc2626',
                  }}>
                  🗑️
                </button>
              </div>
            </div>
            {r.text && (
              <p style={{ fontSize: 14, color: '#374151', lineHeight: 1.6, margin: 0 }}>{r.text}</p>
            )}
          </div>
        ))
      )}
    </div>
  );
}
